import { parseISO } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

// Eastern Time - matches Rails backend config.time_zone = "Eastern Time (US & Canada)"
// This is the fallback timezone when user profile timezone is not set.
export const EASTERN_TIMEZONE = 'America/New_York';

/**
 * Rails `ActiveSupport::TimeZone::MAPPING` — human-friendly names → IANA zones.
 *
 * Rails stores `User#time_zone` in the friendly form ("Pacific Time (US & Canada)")
 * via the `before_validation :convert_time_zone_to_proper_format` hook in
 * `app/models/user.rb`, which rewrites any IANA input back to the MAPPING key.
 * date-fns-tz, however, only understands IANA identifiers — passing a friendly
 * name silently produces an Invalid Date, cascading into `RangeError: Invalid
 * time value` crashes (Honeybadger fault 129787676) and raw-ISO displays.
 *
 * Source: https://github.com/rails/rails/blob/main/activesupport/lib/active_support/values/time_zone.rb
 * Kept in sync with Rails' authoritative table; entries here have been stable for years.
 */
const RAILS_TZ_TO_IANA: Readonly<Record<string, string>> = {
  'International Date Line West': 'Etc/GMT+12',
  'Midway Island': 'Pacific/Midway',
  'American Samoa': 'Pacific/Pago_Pago',
  Hawaii: 'Pacific/Honolulu',
  Alaska: 'America/Juneau',
  'Pacific Time (US & Canada)': 'America/Los_Angeles',
  Tijuana: 'America/Tijuana',
  'Mountain Time (US & Canada)': 'America/Denver',
  Arizona: 'America/Phoenix',
  Chihuahua: 'America/Chihuahua',
  Mazatlan: 'America/Mazatlan',
  'Central Time (US & Canada)': 'America/Chicago',
  Saskatchewan: 'America/Regina',
  Guadalajara: 'America/Mexico_City',
  'Mexico City': 'America/Mexico_City',
  Monterrey: 'America/Monterrey',
  'Central America': 'America/Guatemala',
  'Eastern Time (US & Canada)': 'America/New_York',
  'Indiana (East)': 'America/Indiana/Indianapolis',
  Bogota: 'America/Bogota',
  Lima: 'America/Lima',
  Quito: 'America/Lima',
  'Atlantic Time (Canada)': 'America/Halifax',
  Caracas: 'America/Caracas',
  'La Paz': 'America/La_Paz',
  Santiago: 'America/Santiago',
  Asuncion: 'America/Asuncion',
  Newfoundland: 'America/St_Johns',
  Brasilia: 'America/Sao_Paulo',
  'Buenos Aires': 'America/Argentina/Buenos_Aires',
  Montevideo: 'America/Montevideo',
  Georgetown: 'America/Guyana',
  'Puerto Rico': 'America/Puerto_Rico',
  Greenland: 'America/Nuuk',
  'Mid-Atlantic': 'Atlantic/South_Georgia',
  Azores: 'Atlantic/Azores',
  'Cape Verde Is.': 'Atlantic/Cape_Verde',
  Dublin: 'Europe/Dublin',
  Edinburgh: 'Europe/London',
  Lisbon: 'Europe/Lisbon',
  London: 'Europe/London',
  Casablanca: 'Africa/Casablanca',
  Monrovia: 'Africa/Monrovia',
  UTC: 'Etc/UTC',
  Belgrade: 'Europe/Belgrade',
  Bratislava: 'Europe/Bratislava',
  Budapest: 'Europe/Budapest',
  Ljubljana: 'Europe/Ljubljana',
  Prague: 'Europe/Prague',
  Sarajevo: 'Europe/Sarajevo',
  Skopje: 'Europe/Skopje',
  Warsaw: 'Europe/Warsaw',
  Zagreb: 'Europe/Zagreb',
  Brussels: 'Europe/Brussels',
  Copenhagen: 'Europe/Copenhagen',
  Madrid: 'Europe/Madrid',
  Paris: 'Europe/Paris',
  Amsterdam: 'Europe/Amsterdam',
  Berlin: 'Europe/Berlin',
  Bern: 'Europe/Zurich',
  Zurich: 'Europe/Zurich',
  Rome: 'Europe/Rome',
  Stockholm: 'Europe/Stockholm',
  Vienna: 'Europe/Vienna',
  'West Central Africa': 'Africa/Algiers',
  Bucharest: 'Europe/Bucharest',
  Cairo: 'Africa/Cairo',
  Helsinki: 'Europe/Helsinki',
  Kyiv: 'Europe/Kiev',
  Riga: 'Europe/Riga',
  Sofia: 'Europe/Sofia',
  Tallinn: 'Europe/Tallinn',
  Vilnius: 'Europe/Vilnius',
  Athens: 'Europe/Athens',
  Istanbul: 'Europe/Istanbul',
  Minsk: 'Europe/Minsk',
  Jerusalem: 'Asia/Jerusalem',
  Harare: 'Africa/Harare',
  Pretoria: 'Africa/Johannesburg',
  Kaliningrad: 'Europe/Kaliningrad',
  Moscow: 'Europe/Moscow',
  'St. Petersburg': 'Europe/Moscow',
  Volgograd: 'Europe/Volgograd',
  Samara: 'Europe/Samara',
  Kuwait: 'Asia/Kuwait',
  Riyadh: 'Asia/Riyadh',
  Nairobi: 'Africa/Nairobi',
  Baghdad: 'Asia/Baghdad',
  Tehran: 'Asia/Tehran',
  'Abu Dhabi': 'Asia/Muscat',
  Muscat: 'Asia/Muscat',
  Baku: 'Asia/Baku',
  Tbilisi: 'Asia/Tbilisi',
  Yerevan: 'Asia/Yerevan',
  Kabul: 'Asia/Kabul',
  Ekaterinburg: 'Asia/Yekaterinburg',
  Islamabad: 'Asia/Karachi',
  Karachi: 'Asia/Karachi',
  Tashkent: 'Asia/Tashkent',
  Chennai: 'Asia/Kolkata',
  Kolkata: 'Asia/Kolkata',
  Mumbai: 'Asia/Kolkata',
  'New Delhi': 'Asia/Kolkata',
  Kathmandu: 'Asia/Kathmandu',
  Dhaka: 'Asia/Dhaka',
  'Sri Jayawardenepura': 'Asia/Colombo',
  Almaty: 'Asia/Almaty',
  Astana: 'Asia/Almaty',
  Novosibirsk: 'Asia/Novosibirsk',
  Rangoon: 'Asia/Rangoon',
  Bangkok: 'Asia/Bangkok',
  Hanoi: 'Asia/Bangkok',
  Jakarta: 'Asia/Jakarta',
  Krasnoyarsk: 'Asia/Krasnoyarsk',
  Beijing: 'Asia/Shanghai',
  Chongqing: 'Asia/Chongqing',
  'Hong Kong': 'Asia/Hong_Kong',
  Urumqi: 'Asia/Urumqi',
  'Kuala Lumpur': 'Asia/Kuala_Lumpur',
  Singapore: 'Asia/Singapore',
  Taipei: 'Asia/Taipei',
  Perth: 'Australia/Perth',
  Irkutsk: 'Asia/Irkutsk',
  Ulaanbaatar: 'Asia/Ulaanbaatar',
  Seoul: 'Asia/Seoul',
  Osaka: 'Asia/Tokyo',
  Sapporo: 'Asia/Tokyo',
  Tokyo: 'Asia/Tokyo',
  Yakutsk: 'Asia/Yakutsk',
  Darwin: 'Australia/Darwin',
  Adelaide: 'Australia/Adelaide',
  Canberra: 'Australia/Canberra',
  Melbourne: 'Australia/Melbourne',
  Sydney: 'Australia/Sydney',
  Brisbane: 'Australia/Brisbane',
  Hobart: 'Australia/Hobart',
  Vladivostok: 'Asia/Vladivostok',
  Guam: 'Pacific/Guam',
  'Port Moresby': 'Pacific/Port_Moresby',
  Magadan: 'Asia/Magadan',
  Srednekolymsk: 'Asia/Srednekolymsk',
  'Solomon Is.': 'Pacific/Guadalcanal',
  'New Caledonia': 'Pacific/Noumea',
  Fiji: 'Pacific/Fiji',
  Kamchatka: 'Asia/Kamchatka',
  'Marshall Is.': 'Pacific/Majuro',
  Auckland: 'Pacific/Auckland',
  Wellington: 'Pacific/Auckland',
  "Nuku'alofa": 'Pacific/Tongatapu',
  'Tokelau Is.': 'Pacific/Fakaofo',
  'Chatham Is.': 'Pacific/Chatham',
  Samoa: 'Pacific/Apia',
};

/**
 * Validate an IANA identifier by constructing an Intl.DateTimeFormat with it.
 * An invalid zone throws a RangeError; a valid zone returns the formatter.
 */
const isValidIanaTimezone = (tz: string): boolean => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalize any user-supplied timezone string to a valid IANA identifier.
 *
 * - Rails-friendly names (`"Pacific Time (US & Canada)"`, `"Tokyo"`, `"UTC"`) →
 *   looked up in `RAILS_TZ_TO_IANA`.
 * - Already-IANA names (`"America/Los_Angeles"`, `"Asia/Tokyo"`) → passed through
 *   after Intl validation.
 * - Anything else (empty, `null`, garbage, retired zone) → falls back to
 *   `EASTERN_TIMEZONE` so date-fns-tz never sees an Invalid-Date-producing input.
 */
export const normalizeTimezoneToIana = (tz: string | null | undefined): string => {
  if (!tz) return EASTERN_TIMEZONE;
  const candidate = RAILS_TZ_TO_IANA[tz] ?? tz;
  return isValidIanaTimezone(candidate) ? candidate : EASTERN_TIMEZONE;
};

/**
 * Get the effective timezone for date operations against date-fns-tz.
 *
 * Logic mirrors Rails `ApplicationController#user_time_zone`:
 *   Time.use_zone(current_user.time_zone || Rails.application.config.time_zone, &)
 *
 * 1. Primary: user's profile timezone, canonicalized to IANA.
 * 2. Fallback: Eastern Time (Rails app default) — *not* the browser zone,
 *    which is unreliable under VPN/proxy.
 *
 * This function always returns a valid IANA identifier — safe to pass to
 * `toZonedTime`, `fromZonedTime`, `formatTz`, etc.
 */
export const getEffectiveTimezone = (currentUser: any): string =>
  normalizeTimezoneToIana(currentUser?.timeZone);

/**
 * Format a datetime value in the user's effective timezone.
 * Uses user profile timezone if set, otherwise falls back to Eastern Time.
 * Accepts either an ISO string or a Date (defensive — some callers pass Date).
 */
export const formatDateTimeInUserTimezone = (
  dateInput: string | Date | undefined | null,
  currentUser: any,
  formatStr: string = "MMM d, yyyy 'at' h:mmaaa"
): string => {
  if (!dateInput) return 'N/A';

  try {
    const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    if (Number.isNaN(date.getTime())) return String(dateInput);
    const timezone = getEffectiveTimezone(currentUser);
    const zonedDate = toZonedTime(date, timezone);
    return formatTz(zonedDate, formatStr, { timeZone: timezone });
  } catch {
    return typeof dateInput === 'string' ? dateInput : String(dateInput);
  }
};

/**
 * Format a date (without time) in the user's effective timezone.
 * Uses user profile timezone if set, otherwise falls back to Eastern Time.
 */
export const formatDateInUserTimezone = (
  dateString: string | undefined | null,
  currentUser: any,
  formatStr: string = 'MMM d, yyyy'
): string => {
  if (!dateString) return 'N/A';

  try {
    const date = parseISO(dateString);
    const timezone = getEffectiveTimezone(currentUser);
    const zonedDate = toZonedTime(date, timezone);
    return formatTz(zonedDate, formatStr, { timeZone: timezone });
  } catch {
    return dateString;
  }
};

/**
 * Human-friendly label for the "All times in {tz}" banner. Intentionally
 * returns the user's original (Rails-friendly) string verbatim — e.g.
 * "Pacific Time (US & Canada)" rather than the IANA `"America/Los_Angeles"` —
 * to preserve UX parity with V1/V2. Date operations should not use this.
 */
export const getTimezoneDisplayName = (currentUser: any): string => {
  if (currentUser?.timeZone) {
    return currentUser.timeZone;
  }
  return `${EASTERN_TIMEZONE} (default)`;
};

/**
 * Format a datetime string with an explicit IANA timezone.
 * This is useful when the timezone is provided by the backend API
 * (e.g., in Admin2 JSON responses) rather than from currentUser context.
 *
 * The `timezone` argument is normalized via `normalizeTimezoneToIana`, so
 * callers may pass either IANA or Rails-friendly names.
 *
 * Format matches Rails DateFormatter::DATETIME_FORMAT = "%b %-d, %Y at %l:%M%P"
 * which produces: "Aug 5, 2025 at 11:42pm"
 */
export const formatDateTimeWithTimezone = (
  dateString: string | undefined | null,
  timezone: string,
  formatStr: string = "MMM d, yyyy 'at' h:mmaaa"
): string => {
  if (!dateString) return 'N/A';

  try {
    const date = parseISO(dateString);
    const effectiveTimezone = normalizeTimezoneToIana(timezone);
    const zonedDate = toZonedTime(date, effectiveTimezone);
    return formatTz(zonedDate, formatStr, { timeZone: effectiveTimezone });
  } catch {
    return dateString;
  }
};
